import React, { useCallback, useEffect, useState } from "react";
import {
  ArrowPathIcon,
  XMarkIcon,
  PlusIcon,
  PencilSquareIcon,
  TrashIcon,
  ExclamationTriangleIcon,
  PhotoIcon,
  InformationCircleIcon,
} from "@heroicons/react/24/outline";
import { listAdminRubrics, createRubric, updateRubric, deleteRubric, updateAdminCategory } from "../../api/categoriesAdmin";

const fmt = (n) => Number(n || 0).toLocaleString("ru-RU");
const inputCls =
  "w-full rounded-lg border border-border-primary bg-bg-primary px-3 py-2 text-sm text-text-primary " +
  "placeholder:text-text-muted focus:border-[#4F45E5] focus:outline-none focus:ring-2 focus:ring-[#4F45E5]/30";
const FIELDS = [
  ["name_ru", "Название (RU) *"],
  ["name_kz", "Атауы (KZ)"],
  ["name_en", "Name (EN)"],
  ["name_ch", "名称 (CN)"],
];

const RubricDialog = ({ rubric, onClose, onSaved }) => {
  const [form, setForm] = useState(Object.fromEntries([...FIELDS.map(([k]) => k), "image_url"].map((k) => [k, rubric?.[k] || ""])));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const save = async (e) => {
    e.preventDefault();
    if (!form.name_ru.trim()) return setError("Название (RU) обязательно.");
    setBusy(true);
    try {
      const body = Object.fromEntries(Object.entries(form).map(([k, v]) => [k, v.trim() || (k === "name_ru" ? v : null)]));
      if (rubric) await updateRubric(rubric.id, body);
      else await createRubric(body);
      onSaved(rubric ? `Рубрика «${form.name_ru}» сохранена` : `Рубрика «${form.name_ru}» создана`);
    } catch (err) {
      setError(err.message);
      setBusy(false);
    }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" role="presentation" onMouseDown={(e) => e.target === e.currentTarget && !busy && onClose()}>
      <form onSubmit={save} role="dialog" aria-modal="true" className="w-full max-w-lg rounded-block border border-border-primary bg-bg-card text-left shadow-lg">
        <div className="flex items-center justify-between border-b border-border-primary px-5 py-3.5">
          <h3 className="text-base font-semibold text-text-primary">
            {rubric ? (
              <>
                Рубрика <span className="font-mono text-text-muted">#{rubric.id}</span>
              </>
            ) : (
              "Новая рубрика"
            )}
          </h3>
          <button type="button" onClick={onClose} aria-label="Закрыть" className="rounded-lg p-1 text-text-muted hover:bg-bg-tertiary">
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>
        <div className="space-y-3 p-5">
          {FIELDS.map(([k, l], i) => (
            <label key={k} className="block">
              <span className="mb-1 block text-xs font-medium text-text-secondary">{l}</span>
              <input autoFocus={i === 0} value={form[k]} onChange={(e) => setForm((f) => ({ ...f, [k]: e.target.value }))} className={inputCls} />
            </label>
          ))}
          <label className="block">
            <span className="mb-1 block text-xs font-medium text-text-secondary">Изображение (URL)</span>
            <div className="flex gap-2">
              <input value={form.image_url} onChange={(e) => setForm((f) => ({ ...f, image_url: e.target.value }))} placeholder="https://…" className={inputCls} />
              {form.image_url && <img src={form.image_url} alt="" className="h-10 w-10 shrink-0 rounded-lg object-cover" />}
            </div>
          </label>
          {error && <p className="text-sm text-rose-500">{error}</p>}
        </div>
        <div className="flex justify-end gap-2 border-t border-border-primary px-5 py-3">
          <button type="button" onClick={onClose} className="rounded-lg border border-border-primary px-4 py-2 text-sm text-text-secondary hover:bg-bg-tertiary">
            Отмена
          </button>
          <button type="submit" disabled={busy} className="inline-flex items-center gap-2 rounded-lg bg-accent-grad px-4 py-2 text-sm font-semibold text-white hover:opacity-90 disabled:opacity-50">
            {busy && <ArrowPathIcon className="h-4 w-4 animate-spin" />}
            {rubric ? "Сохранить" : "Создать"}
          </button>
        </div>
      </form>
    </div>
  );
};

/** Категорияны рубрикаға қосу — тек көрінетін, басқа рубрикада емес немесе кез келген категория. */
const AddCategorySelect = ({ rubric, allCategories, onPick, disabled }) => (
  <select
    value=""
    disabled={disabled}
    onChange={(e) => e.target.value && onPick(Number(e.target.value))}
    aria-label={`Добавить категорию в «${rubric.name_ru}»`}
    className="rounded-lg border border-dashed border-border-primary bg-transparent px-2 py-1 text-xs text-text-secondary hover:border-[#4F45E5] focus:outline-none"
  >
    <option value="">+ категория</option>
    {allCategories
      .filter((c) => c.rubric_id !== rubric.id && !c.is_hidden)
      .map((c) => (
        <option key={c.id} value={c.id}>
          {c.name} #{c.id}
          {c.rubric_name ? ` (из «${c.rubric_name}»)` : ""}
        </option>
      ))}
  </select>
);

const RubricsPanel = ({ onCategoriesChanged }) => {
  const [data, setData] = useState({ items: [], unassigned: [] });
  const [state, setState] = useState("loading");
  const [error, setError] = useState(null);
  const [toast, setToast] = useState(null);
  const [dialog, setDialog] = useState(null); // { rubric } | { rubric: null }
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!toast || toast.type === "error") return undefined;
    const t = setTimeout(() => setToast(null), 4000);
    return () => clearTimeout(t);
  }, [toast]);

  const load = useCallback(async () => {
    try {
      setData(await listAdminRubrics());
      setState("ready");
    } catch (err) {
      setError(err.message);
      setState("error");
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const rubricName = Object.fromEntries(data.items.map((r) => [r.id, r.name_ru]));
  const allCategories = [
    ...data.items.flatMap((r) => r.categories.map((c) => ({ ...c, rubric_id: r.id, rubric_name: r.name_ru }))),
    ...data.unassigned.map((c) => ({ ...c, rubric_id: null, rubric_name: null })),
  ].sort((a, b) => a.name.localeCompare(b.name, "ru"));

  const act = async (fn, text) => {
    setBusy(true);
    try {
      await fn();
      setToast({ type: "success", text });
      await load();
      onCategoriesChanged?.();
    } catch (err) {
      setToast({ type: "error", text: err.message });
    } finally {
      setBusy(false);
    }
  };

  const assign = (categoryId, rubricId) => {
    const c = allCategories.find((x) => x.id === categoryId);
    act(
      () => updateAdminCategory(categoryId, { rubric_id: rubricId }),
      rubricId ? `«${c?.name}» → рубрика «${rubricName[rubricId]}»` : `«${c?.name}» убрана из рубрики`
    );
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <p className="flex max-w-3xl items-start gap-2 rounded-lg border border-border-primary bg-bg-card px-4 py-2.5 text-xs text-text-secondary">
          <InformationCircleIcon className="h-4 w-4 shrink-0 text-text-muted" />
          Рубрики — группы категорий на главной сайта. Категория без рубрики не видна в этих группах. Порядок рубрик и категорий внутри — как во вкладке «Категории» (закреплённые, затем популярность).
        </p>
        <button type="button" onClick={() => setDialog({ rubric: null })} className="inline-flex items-center gap-1.5 rounded-lg bg-accent-grad px-3 py-2 text-sm font-semibold text-white hover:opacity-90">
          <PlusIcon className="h-4 w-4" /> Рубрика
        </button>
      </div>

      {toast && (
        <div role={toast.type === "error" ? "alert" : "status"} className={`flex items-start justify-between gap-3 rounded-lg border px-4 py-3 text-sm ${toast.type === "error" ? "border-rose-500/30 bg-rose-500/10 text-rose-500" : "border-emerald-500/30 bg-emerald-500/10 text-emerald-500"}`}>
          <span>{toast.text}</span>
          <button type="button" onClick={() => setToast(null)} aria-label="Закрыть" className="shrink-0 opacity-70 hover:opacity-100">
            <XMarkIcon className="h-4 w-4" />
          </button>
        </div>
      )}

      {data.unassigned.length > 0 && (
        <section className="rounded-block border border-amber-500/30 bg-amber-500/[0.07] p-4">
          <h3 className="flex items-center gap-2 text-sm font-semibold text-amber-500">
            <ExclamationTriangleIcon className="h-5 w-5" /> Без рубрики: {data.unassigned.length}
          </h3>
          <p className="mt-0.5 text-xs text-text-secondary">Эти категории не видны в группах на главной сайта. Выберите для каждой рубрику.</p>
          <ul className="mt-3 flex flex-wrap gap-2">
            {data.unassigned.map((c) => (
              <li key={c.id} className="flex items-center gap-2 rounded-lg border border-border-primary bg-bg-card px-3 py-1.5 text-xs">
                <span className="text-text-primary">{c.name}</span>
                <span className="text-text-muted">
                  #{c.id} · {fmt(c.announcements)} объявл.
                </span>
                <select value="" disabled={busy} onChange={(e) => e.target.value && assign(c.id, Number(e.target.value))} aria-label={`Рубрика для «${c.name}»`} className="rounded-md border border-border-primary bg-bg-primary px-1.5 py-0.5 text-xs text-text-primary">
                  <option value="">в рубрику…</option>
                  {data.items.map((r) => (
                    <option key={r.id} value={r.id}>
                      {r.name_ru}
                    </option>
                  ))}
                </select>
              </li>
            ))}
          </ul>
        </section>
      )}

      {state === "error" ? (
        <div className="rounded-block border border-border-primary bg-bg-card px-6 py-14 text-center text-sm text-text-secondary">
          Не удалось загрузить рубрики: {error}
          <button type="button" onClick={load} className="ml-2 underline">
            Повторить
          </button>
        </div>
      ) : state === "loading" ? (
        <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="h-28 animate-pulse rounded-block bg-bg-card" />
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
          {data.items.map((r) => {
            const visible = r.categories.filter((c) => !c.is_hidden);
            const hiddenCount = r.categories.length - visible.length;
            return (
              <section key={r.id} className={`rounded-block border border-border-primary bg-bg-card p-4 shadow-sm ${busy ? "opacity-80" : ""}`}>
                <div className="flex items-start gap-3">
                  {r.image_url ? (
                    <img src={r.image_url} alt="" loading="lazy" className="h-10 w-10 shrink-0 rounded-lg bg-bg-tertiary object-cover" />
                  ) : (
                    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-bg-tertiary text-text-muted">
                      <PhotoIcon className="h-5 w-5" />
                    </span>
                  )}
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-1.5">
                      <h3 className="truncate text-sm font-semibold text-text-primary">{r.name_ru}</h3>
                      <span className="font-mono text-[11px] text-text-muted">#{r.id}</span>
                    </div>
                    <p className="truncate text-[11px] text-text-muted">
                      {r.name_kz || "— KZ"} · {r.name_en || "— EN"}
                    </p>
                  </div>
                  {confirmDelete === r.id ? (
                    <div className="flex items-center gap-1.5">
                      <span className="text-xs text-text-secondary">Удалить?</span>
                      <button type="button" disabled={busy} onClick={() => act(() => deleteRubric(r.id), `Рубрика «${r.name_ru}» удалена`).then(() => setConfirmDelete(null))} className="rounded-lg bg-rose-500 px-2.5 py-1 text-xs font-semibold text-white hover:bg-rose-600">
                        Да
                      </button>
                      <button type="button" onClick={() => setConfirmDelete(null)} className="rounded-lg border border-border-primary px-2.5 py-1 text-xs text-text-secondary hover:bg-bg-tertiary">
                        Нет
                      </button>
                    </div>
                  ) : (
                    <div className="flex items-center gap-0.5">
                      <button type="button" onClick={() => setDialog({ rubric: r })} title="Редактировать" aria-label="Редактировать" className="rounded-lg p-1.5 text-text-muted hover:bg-bg-tertiary hover:text-text-primary">
                        <PencilSquareIcon className="h-4 w-4" />
                      </button>
                      <button
                        type="button"
                        onClick={() => setConfirmDelete(r.id)}
                        disabled={r.categories.length > 0}
                        title={r.categories.length ? "Сначала перенесите категории в другую рубрику" : "Удалить рубрику"}
                        aria-label="Удалить"
                        className="rounded-lg p-1.5 text-text-muted hover:bg-bg-tertiary hover:text-rose-500 disabled:opacity-30"
                      >
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    </div>
                  )}
                </div>
                <ul className="mt-3 flex flex-wrap items-center gap-1.5">
                  {visible.map((c) => (
                    <li key={c.id} className="group flex items-center gap-1 rounded-full border border-border-primary bg-bg-primary py-0.5 pl-2.5 pr-1 text-xs text-text-primary">
                      {c.name}
                      <span className="font-mono text-[10px] text-text-muted">#{c.id}</span>
                      <button type="button" disabled={busy} onClick={() => assign(c.id, null)} title="Убрать из рубрики" aria-label={`Убрать «${c.name}» из рубрики`} className="rounded-full p-0.5 text-text-muted hover:bg-rose-500/10 hover:text-rose-500">
                        <XMarkIcon className="h-3 w-3" />
                      </button>
                    </li>
                  ))}
                  {visible.length === 0 && <li className="text-xs text-text-muted">Нет видимых категорий — рубрика пустая на сайте</li>}
                  <li>
                    <AddCategorySelect rubric={r} allCategories={allCategories} disabled={busy} onPick={(id) => assign(id, r.id)} />
                  </li>
                </ul>
                {hiddenCount > 0 && <p className="mt-2 text-[11px] text-text-muted">+ скрытых категорий: {hiddenCount}</p>}
              </section>
            );
          })}
        </div>
      )}

      {dialog && (
        <RubricDialog
          rubric={dialog.rubric}
          onClose={() => setDialog(null)}
          onSaved={(text) => {
            setDialog(null);
            setToast({ type: "success", text });
            load();
          }}
        />
      )}
    </div>
  );
};

export default RubricsPanel;
